import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../../context/AuthContext';
import NotificationBell from '../../Notifications/NotificationBell'; // NEW
import './styles/MobileMenu.css';

// Small inline icons so the menu doesn't need an icon-library dependency.
const CloudIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.4 1.6A4 4 0 0 0 6.5 19h11z" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3l7 3.5v5c0 4.5-3 7.5-7 9.5-4-2-7-5-7-9.5v-5L12 3z" />
    <path d="M9.5 12l1.8 1.8L14.8 10" />
  </svg>
);

const WrenchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2 2.5-2.5z" />
  </svg>
);

const CompassIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M14.8 9.2l-2 4.7-4.7 2 2-4.7 4.7-2z" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`mobile-menu__chevron-icon ${open ? 'is-open' : ''}`}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// Services are grouped so the panel reads as a few sections instead of one
// long undifferentiated list of 19 links.
const SERVICE_GROUPS = [
  {
    id: 'cloud-infrastructure',
    label: 'Cloud & Infrastructure',
    icon: <CloudIcon />,
    items: [
      ['/services/Cloud-IT-Solutions', 'Cloud IT Solutions'],
      ['/services/Managed-Network-Solutions', 'Network Solutions'],
      ['/services/Network-Audit', 'Network Audit'],
      ['/services/Data-Center-Colocation-Services', 'Data Center Colocation'],
      ['/services/Help-finding-Ideal-Colocation', 'Help finding Ideal Colocation'],
      ['/services/Design-and-Deployment', 'Design and Deployment'],
      ['/services/Configuration-Services-Deployment', 'Configuration Services Deployment'],
    ],
  },
  {
    id: 'ai-security',
    label: 'AI & Security',
    icon: <ShieldIcon />,
    items: [
      ['/services/AI-Automation-Services', 'AI & Automation'],
      ['/services/Cybersecurity-Services', 'Cyber Security'],
      ['/services/Cybersecurity-Consulting', 'Cybersecurity Consulting'],
    ],
  },
  {
    id: 'support-delivery',
    label: 'Support & Delivery',
    icon: <WrenchIcon />,
    items: [
      ['/services/IT-Managed-Support-Services', 'IT Support Services'],
      ['/services/Support-Training', 'Support & Training'],
      ['/services/Implementation-Services', 'Implementation Services'],
      ['/services/Technical-Recruiters', 'Technical Recruiters'],
    ],
  },
  {
    id: 'strategy-consulting',
    label: 'Strategy & Consulting',
    icon: <CompassIcon />,
    items: [
      ['/services/Strategic-IT-Consulting', 'Strategic IT Consulting'],
      ['/services/Business-Strategy', 'Business Strategy'],
      ['/services/Cloud-Consulting', 'Cloud Consulting'],
      ['/services/Infrastructure-Consulting', 'Infrastructure Consulting'],
      ['/services/Digital-Transformation', 'Digital Transformation'],
    ],
  },
];

// Top-level links that don't have a sub-menu.
const NAV_LINKS = [
  ['/Partners', 'Partners'],
  ['/About', 'About Us'],
  ['/blogs', 'Blogs & Resources'],
  ['/contact', 'Contact Us'],
];

const MobileMenu = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [servicesOpen, setServicesOpen] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const isAdminSupport = user?.role === 'ADMIN_SUPPORT';
  const isMember = user?.role === 'MEMBER';

  const menuVariants = {
    hidden: { opacity: 0, y: -12 },
    visible: { opacity: 1, y: 0, transition: { type: 'tween', duration: 0.22 } },
    exit: { opacity: 0, y: -12, transition: { type: 'tween', duration: 0.18 } },
  };

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  const handleLogout = async () => {
    await logout();
    onClose?.();
    navigate('/login');
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <motion.aside
      className="mobile-menu"
      variants={menuVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      aria-label="Mobile navigation"
    >
    
      <div className="mobile-menu__content">
        <nav className="mobile-menu__nav" aria-label="Primary">
          <Link
            to="/Hardware-products"
            className={`mobile-menu__nav-item ${isActive('/') ? 'is-active' : ''}`}
            onClick={onClose}
          >
            Hardware
          </Link>

          <div className="mobile-menu__nav-row">
            <Link
              to="/services"
              className={`mobile-menu__nav-item ${
                isActive('/services') ? 'is-active' : ''
              }`}
              onClick={onClose}
            >
              Services
            </Link>
            <button
              type="button"
              className="mobile-menu__chevron-btn"
              onClick={() => setServicesOpen((prev) => !prev)}
              aria-expanded={servicesOpen}
              aria-label={servicesOpen ? 'Collapse services' : 'Expand services'}
            >
              <ChevronIcon open={servicesOpen} />
            </button>
          </div>

          {servicesOpen ? (
            <div className="mobile-menu__panel">
              <span className="mobile-menu__panel-eyebrow">Services</span>

              {SERVICE_GROUPS.map((group) => (
                <div className="mobile-menu__group" key={group.id}>
                  <div className="mobile-menu__group-header">
                    <span className="mobile-menu__group-icon" aria-hidden="true">
                      {group.icon}
                    </span>
                    <span className="mobile-menu__group-label">{group.label}</span>
                  </div>

                  <div className="mobile-menu__group-items">
                    {group.items.map(([href, label]) => (
                      <Link
                        key={href}
                        to={href}
                        className={`mobile-menu__group-item ${
                          isActive(href) ? 'is-active' : ''
                        }`}
                        onClick={onClose}
                      >
                        <span>{label}</span>
                        <span className="mobile-menu__group-arrow" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 6l6 6-6 6" />
                          </svg>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {NAV_LINKS.filter(([href]) => href !== '/').map(([href, label]) => (
            <Link
              key={href}
              to={href}
              className={`mobile-menu__nav-item ${isActive(href) ? 'is-active' : ''}`}
              onClick={onClose}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Auth / account */}
        {!user ? (
          <div className="mobile-menu__auth">
            <button
              type="button"
              className="mobile-menu__auth-btn mobile-menu__auth-btn--ghost"
              onClick={() => go('/login')}
            >
              Login
            </button>
            <button
              type="button"
              className="mobile-menu__auth-btn mobile-menu__auth-btn--solid"
              onClick={() => go('/register')}
            >
              Sign up
            </button>
          </div>
        ) : (
          <>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="mobile-menu__cta-btn"
              onClick={() => go('/contact')}
            >
              Let&apos;s Meet
            </motion.button>

            <div className="mobile-menu__section mobile-menu__account">
              <NotificationBell /> {/* NEW */}

              {isAdmin && (
                <Link to="/admin" className="mobile-menu__item" onClick={onClose}>
                  Admin Panel
                </Link>
              )}
              {(isAdmin || isMember) && (
                <Link to="/certifications/overview" className="mobile-menu__item" onClick={onClose}>
                  Certification Overview
                </Link>
              )}
              {(isMember || isAdmin) && (
                <Link to="/member/certifications" className="mobile-menu__item" onClick={onClose}>
                  My Certifications
                </Link>
              )}
              {isAdmin && (
                <Link to="/admin/blogs/questions" className="mobile-menu__item" onClick={onClose}>
                  Manage Q&amp;A
                </Link>
              )}
              {(isAdmin || isAdminSupport) && (
                <Link to="/admin/support" className="mobile-menu__item" onClick={onClose}>
                  Manage Support Content
                </Link>
              )}
              {isMember && (
                <Link to="/member/blogs" className="mobile-menu__item" onClick={onClose}>
                  Manage My Blogs
                </Link>
              )}
              {isMember && (
                <Link to="/member/blogs/questions" className="mobile-menu__item" onClick={onClose}>
                  Manage Q&amp;A
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin/knowledge-base"
                  className="mobile-menu__item"
                  onClick={onClose}
                >
                  Manage Knowledge Base
                </Link>
              )}
              <Link to="/settings" className="mobile-menu__item" onClick={onClose}>
                Settings
              </Link>
              <Link to="/tickets" className="mobile-menu__item" onClick={onClose}>
                Support Tickets
              </Link>
              <button type="button" className="mobile-menu__logout" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </>
        )}
      </div>
    </motion.aside>
  );
};

export default MobileMenu;